import { getEntity, getEntitySubdomain } from '@opengov/app-core/entity/selectors';
import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/launchDarkly';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { IEntity } from '@opengov/og-api-ts';
import {
  FLAG_STORIES_CONTENT_BREAKS_AND_PADDING,
  FLAG_STORIES_PAGE_BREAK_EXPORTS,
} from '@opengov/stories-shared/constants';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { storiesMsgs } from '../../locale';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { StoryPreview, IStoryPreviewProps } from '../components/StoryPreview';
import {
  selectAddPropsalsToStoriesFlag,
  selectUseAiToGenerateContentFlag,
  selectStoriesSummarizerGAFlag,
  selectStoriesOgAssistGenerateSummaryFlag,
  selectIsAiToggleFeatureEnabled,
  selectStoriesProgressTrackerFlag,
  selectIsCustomSpreadsheetReportEnabledFlag,
} from '../common/flags';
import { selectIsTemplate } from '../selectors/storiesSelectors';
import { selectStoryId } from '../selectors/storyViewSelectors';

const selectStoryPreviewProps = createStructuredSelector<any, IStoryPreviewProps>({
  storyId: selectStoryId,
  isTemplate: selectIsTemplate,
  entitySubdomain: getEntitySubdomain,
  entityId: createSelector(getEntity, (entity) => entity.entity_id),
  entityUuid: createSelector(getEntity, (entity) => entity.uuid ?? ''),
  entityName: createSelector(getEntity, (entity) => entity.display_name),
  canEditPageBreaks: createSelector(selectFeatureFlags, (flags) => flags.get(FLAG_STORIES_PAGE_BREAK_EXPORTS)),
  canEditSpacing: createSelector(selectFeatureFlags, (flags) => flags.get(FLAG_STORIES_CONTENT_BREAKS_AND_PADDING)),
  canAddProposalContent: selectAddPropsalsToStoriesFlag,
  canUseAiToGenerateContent: selectUseAiToGenerateContentFlag,
  canUseStoriesSummarizerGA: selectStoriesSummarizerGAFlag,
  canUseOgAssistGenerateSummary: selectStoriesOgAssistGenerateSummaryFlag,
  isAiToggleFeatureEnabled: selectIsAiToggleFeatureEnabled,
  showProgressTracker: selectStoriesProgressTrackerFlag,
  isCustomSpreadsheetReportEnabled: selectIsCustomSpreadsheetReportEnabledFlag,
  openModal: () => (modal: string) => modalActionCreators.setModalComponentType(modal),
  messages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.editor })),
  modalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.modals })),
});

export const storyPreviewRoute = {
  name: 'StoryPreview',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Story', name)),
  selector: selectStoryPreviewProps,
  component: StoryPreview,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation, onSuccess, onError) => {
      const { entitySubdomain }: { entitySubdomain: string } = nextLocation.params;

      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          await coreFeatureFlagEffects.getFlags().then((flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
